import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomLink from '../../components/CustomLink';
import { Button } from '../../components/Button';
import { Logo } from '../../components/Logo';
import { useUser } from '../../shared/use-user';
import { useDocumentTitle } from '../../shared/use-document-title';
import { useAuthForm } from '../../shared/use-auth-form';
import { useRouting } from '../../shared/use-routing';
import { getLegalLinks } from '../../../ui/app-ui-config';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { GuestContactSupportForm } from '../../components/GuestContactSupportForm';
import { EmailStep } from '../../components/EmailStep';
import { OtpStep } from '../../components/OtpStep';
import styles from './SignInPage.css';

function SignInPage() {
    const { userData } = useUser();
    const { t } = useTranslation();
    const [isVisibleSupportForm, setIsVisibleSupportForm] = useState(false);
    const { lang } = useRouting();
    const legalLinks = getLegalLinks(userData.legalRegion, lang);
    const {
        step,
        STEPS,
        fields,
        errors,
        isPending,
        isOtpPending,
        isResent,
        otpCountdown,
        resendCountdown,
        changeField,
        submitEmail,
        resendOtp,
        submitOtp,
        goBack,
        clearFormError
    } = useAuthForm();

    useDocumentTitle('Sign In');

    const handleResendOtp = async e => {
        await resendOtp(e);
    };

    const handleHelpClick = () => {
        setIsVisibleSupportForm(true);
        logEvent(EVENT_TYPES.SIGN_IN_HELP_CLICKED, { step });
    };

    const handleGoBack = () => {
        logEvent(EVENT_TYPES.SIGN_IN_BACK_CLICKED, { step });
        goBack();
    };

    const renderStep = () => {
        if (step === STEPS.EMAIL) {
            return (
                <EmailStep
                    fields={fields}
                    errors={errors}
                    isPending={isPending}
                    onChange={changeField}
                    onSubmit={submitEmail}
                    resendCountdown={resendCountdown}
                    otpCountdown={otpCountdown}
                />
            );
        }

        if (step === STEPS.OTP) {
            return (
                <OtpStep
                    email={fields.email}
                    code={fields.code}
                    errors={errors}
                    isOtpPending={isOtpPending}
                    isPending={isPending}
                    isResent={isResent}
                    resendCountdown={resendCountdown}
                    otpCountdown={otpCountdown}
                    onComplete={submitOtp}
                    onResend={handleResendOtp}
                    onErrorClear={clearFormError}
                />
            );
        }
    };

    return (
        <>
            <div className={styles.wrapper}>
                {step === STEPS.EMAIL && <Logo className={styles.logo} inApp />}
                <form className={styles.form}>
                    {step === STEPS.EMAIL
                        ? <h1 className={styles.title}>Enter your email to continue</h1>
                        : (
                            <Button
                                iconLeft={{ name: 'chevronLeftFilled' }}
                                buttonProps={{ type: 'button', 'aria-label': t('common.goBack') }}
                                type='secondary'
                                onClick={handleGoBack}
                                className={styles.buttonBack}
                            />
                        )
                    }
                    {renderStep()}
                </form>
                <Button
                    label='Need Help signing in?'
                    type='secondary'
                    className={styles.buttonHelp}
                    size='big'
                    buttonProps={{ type: 'button' }}
                    onClick={handleHelpClick}
                />
                {isVisibleSupportForm && (
                    <GuestContactSupportForm
                        onClose={() => setIsVisibleSupportForm(false)}
                        email={fields.email}
                    />
                )}
                {step === STEPS.EMAIL && (
                    <p className={styles.agreementText}>
                        By continuing, you agree to our <br />
                        <CustomLink
                            to={legalLinks.termsOfUse}
                            target='_blank'
                            className={styles.link}
                        >
                            Terms of use
                        </CustomLink>
                        {' '}{t('common.and')}{' '}
                        <CustomLink
                            to={legalLinks.privacyPolicy}
                            target='_blank'
                            className={styles.link}
                        >
                            Privacy policy
                        </CustomLink>
                    </p>
                )}
            </div>
        </>
    );
}

export {
    SignInPage
};
